import { Box, TextButton, Tooltip } from '@wix/design-system';
import { UserContactsSection } from '@wix/wix-help-widget-common/homepage';
import { useTranslation } from '@wix/wix-i18n-config';
import { Email } from '@wix/wix-ui-icons-common';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { CopyIcon } from '../../../common/components/CopyIcon';
import { TLocalesKeys } from '../../../types';
import { biHelpCenter } from '../../../util/bi';
import { userContactsDataHooks } from './UserContacts.dataHooks';

type UserContactsEmailProps = Pick<UserContactsSection, 'email'>;

export const UserContactsEmail: FC<UserContactsEmailProps> = ({ email }) => {
  const { t } = useTranslation();
  const [emailCopied, setEmailCopied] = useState(false);
  const [emailHover, setEmailHover] = useState(false);

  useEffect(() => {
    if (!emailHover) {
      setEmailCopied(false);
    }
  }, [emailHover]);

  const onEmailClick = useCallback(
    () => biHelpCenter.logOpenEmailButtonClicked(),
    [],
  );

  const onEmailCopy = useCallback(() => {
    setEmailCopied(true);
    biHelpCenter.logCopyEmailButtonClicked();
  }, []);

  const onMouseOver = useCallback(() => setEmailHover(true), []);
  const onMouseLeave = useCallback(() => setEmailHover(false), []);

  const emailSuffixIcon = useMemo(() => {
    const tooltipContent = t<TLocalesKeys>(
      emailCopied
        ? 'home_contact_partner_email_copied'
        : 'home_contact_partner_copy_email',
    );

    return (
      <Tooltip
        content={tooltipContent}
        size="small"
        moveBy={{ x: 26 }}
        dataHook={userContactsDataHooks.emailCopyIconTooltip()}
      >
        <CopyIcon text={email} onSuccessCopy={onEmailCopy} />
      </Tooltip>
    );
  }, [email, emailCopied, onEmailCopy, t]);

  return (
    <Box gap={2}>
      <Email fill="#116DFF" size={18} />
      <TextButton
        as="a"
        href={`mailto:${email}`}
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseLeave}
        onClick={onEmailClick}
        skin="dark"
        size="small"
        underline="onHover"
        suffixIcon={emailHover ? emailSuffixIcon : undefined}
        dataHook={userContactsDataHooks.email()}
      >
        {email}
      </TextButton>
    </Box>
  );
};
